import React, { useState, useEffect, useRef } from "react";
import BaseLayout from "./BaseLayout";
import { useNavigate, Link } from "react-router-dom";
import { API_BASE } from "../api";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const SignUpPage = ({ isAuthenticated, user }) => {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password1, setPassword] = useState("");
  const [password2, setPasswordConfirm] = useState("");
  const [message, setMessage] = useState({ type: "", content: "" });
  const [honeypot, setHoneypot] = useState("");
  const loadTime = useRef(Date.now());
  const navigate = useNavigate();
  const { setAuthData } = useAuth();

  useEffect(() => {
    localStorage.removeItem("token");
    setAuthData({ isAuthenticated: false, token: null });
    //console.log("Authentication state cleared");
  }, [setAuthData]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage({ type: "", content: "" });

    // Honeypot: bots fill hidden fields, real users won't
    if (honeypot) {
      setMessage({ type: "success", content: "Account created successfully! Logging you in..." });
      return;
    }

    // Timing: bots submit instantly; humans take at least 3 seconds
    if (Date.now() - loadTime.current < 3000) {
      setMessage({ type: "error", content: "Please take a moment to complete the form." });
      return;
    }

    // Name validation: only letters (including accented), spaces, hyphens, apostrophes
    const nameRegex = /^[a-zA-Z\u00C0-\u017E\s'-]{1,50}$/;
    const urlPattern = /https?:\/\/|www\.|bit\.ly|\.com|\.net|\.org|\.io/i;
    const emojiPattern = /[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}]/u;

    // Username validation (optional — if provided)
    const usernameRegex = /^[a-zA-Z0-9_-]{3,25}$/;
    if (username && !usernameRegex.test(username)) {
      setMessage({ type: "error", content: "Username must be 3–25 characters and may only contain letters, numbers, underscores, and hyphens." });
      return;
    }

    if (!nameRegex.test(firstName) || urlPattern.test(firstName) || emojiPattern.test(firstName)) {
      setMessage({ type: "error", content: "First name contains invalid characters. Only letters, spaces, hyphens, and apostrophes are allowed." });
      return;
    }

    if (!nameRegex.test(lastName) || urlPattern.test(lastName) || emojiPattern.test(lastName)) {
      setMessage({ type: "error", content: "Last name contains invalid characters. Only letters, spaces, hyphens, and apostrophes are allowed." });
      return;
    }

    if (password1 !== password2) {
      setMessage({ type: "error", content: "Passwords do not match." });
      return;
    }

    if (password1.length < 8) {
      setMessage({
        type: "error",
        content: "Password must be at least 8 characters long.",
      });
      return;
    }

    try {
      const signUpResponse = await axios.post(`${API_BASE}/sign-up`, {
        email,
        username: username || undefined,
        firstName,
        lastName,
        password1,
        password2,
      });

      if (signUpResponse.status === 200) {
        const loginResponse = await axios.post(`${API_BASE}/login`, {
          email,
          password: password1,
        });
        const { access_token, user } = loginResponse.data;

        setMessage({
          type: "success",
          content: "Account created successfully! Logging you in...",
        });
        setTimeout(() => {
          localStorage.setItem("token", access_token);
          localStorage.setItem("user", JSON.stringify(user));
          setAuthData({ isAuthenticated: true, token: access_token, user: user });
  
          navigate('/home');
        }, 1500);
      }
    } catch (error) {
      const errorMessage =
        error.response && error.response.data
          ? error.response.data.error
          : "An error occurred. Please try again.";
      setMessage({ type: "error", content: errorMessage });
    }
  };

  return (
    <BaseLayout isAuthenticated={isAuthenticated} user={user}>
      <div className="auth-wrapper">
        <img
          src="/loginlogo.png"
          className="auth-logo"
          alt="FlamePicks"
        />
        <div className="auth-card">
          {message.content && (
            <div
              className={`alert ${message.type === "error" ? "alert-danger" : "alert-success"}`}
              role="alert"
            >
              {message.content}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            {/* Honeypot field — hidden from real users, bots will fill this */}
            <div className="hp-field" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                className="form-control"
                id="email"
                name="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="username">Username <span className="auth-optional-label">(optional)</span></label>
              <input
                type="text"
                className="form-control"
                id="username"
                name="username"
                placeholder="Choose a username (e.g. coolpicker99)"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
              />
            </div>
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                className="form-control"
                id="firstName"
                name="firstName"
                placeholder="Enter first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                className="form-control"
                id="lastName"
                name="lastName"
                placeholder="Enter last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="password1">Password</label>
              <input
                type="password"
                className="form-control"
                id="password1"
                name="password1"
                placeholder="Enter password"
                value={password1}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="password2">Confirm Password</label>
              <input
                type="password"
                className="form-control"
                id="password2"
                name="password2"
                placeholder="Re-enter password"
                value={password2}
                onChange={(e) => setPasswordConfirm(e.target.value)}
              />
            </div>
            <button type="submit" className="auth-submit-btn">
              Create Account
            </button>
          </form>
          <div className="auth-footer-link">
            Already have an account? <Link to="/login">Sign in</Link>
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

export default SignUpPage;
